import { datePl, formatPhone, type MeDto, type SearchResultDto, taskLabel } from '@bqcrm/shared';
import { Anchor, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconBriefcase, IconChecklist, IconMapPin, IconSearch, IconTool, IconUsers } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { api } from '../api';
import { CaseStatusBadge, TaskStatusBadge } from '../components/Badges';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { EmptyState, SectionCard } from '../components/ui';
import { dayLong, formatDateTime } from '../format';

/** Termin pracy: „poniedziałek, 2 listopada 2026” albo „3.11.2026 – 5.11.2026”; bez terminu — „do zaplanowania”. */
const workTerm = (w: { dateFrom: string | null; dateTo: string | null }) =>
  !w.dateFrom ? 'bez terminu' : !w.dateTo || w.dateTo === w.dateFrom ? dayLong(w.dateFrom) : `${datePl(w.dateFrom)} – ${datePl(w.dateTo)}`;

export function SearchPage(_: { me: MeDto }) {
  const [params] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const tooShort = q.length < 2;
  const res = useQuery({ queryKey: ['search', q], queryFn: () => api<SearchResultDto>(`/search?q=${encodeURIComponent(q)}`), enabled: !tooShort });
  const empty = res.data && !res.data.parties.length && !res.data.sites.length && !res.data.cases.length && !res.data.works.length && !res.data.tasks.length;
  const none = <EmptyState compact title="Brak wyników" />;

  return (
    <>
      <PageHeader
        title={tooShort ? 'Wyszukiwanie' : `Wyniki wyszukiwania: „${q}”`}
        description="Szukanie bez rozróżniania polskich znaków i wielkości liter; telefon w dowolnym zapisie."
      />
      {tooShort ? (
        <EmptyState icon={<IconSearch size={24} />} title="Wpisz co najmniej 2 znaki" hint="Szukaj po nazwisku lub nazwie klienta, telefonie, adresie, numerze sprawy albo pracy." />
      ) : (
        <QueryState isLoading={res.isLoading} error={res.error}>
          {empty ? (
            <EmptyState icon={<IconSearch size={24} />} title="Nic nie znaleziono" hint="Sprawdź pisownię albo szukaj po numerze telefonu." />
          ) : (
            res.data && (
              <SimpleGrid cols={{ base: 1, md: 3 }}>
                <SectionCard title="Klienci" icon={<IconUsers size={16} />}>
                  <Stack gap={6}>
                    {res.data.parties.length === 0 && none}
                    {res.data.parties.map((p) => (
                      <div key={p.id}>
                        <Anchor component={Link} to={`/klienci/${p.id}`} fw={600} size="sm">
                          {p.displayName}
                        </Anchor>
                        <Text size="xs" c="dimmed">
                          {[p.city, ...p.phones.map(formatPhone)].filter(Boolean).join(' · ')}
                        </Text>
                      </div>
                    ))}
                  </Stack>
                </SectionCard>
                <SectionCard title="Lokalizacje" icon={<IconMapPin size={16} />}>
                  <Stack gap={6}>
                    {res.data.sites.length === 0 && none}
                    {res.data.sites.map((s) => (
                      <div key={s.id}>
                        <Anchor component={Link} to={`/lokalizacje/${s.id}`} fw={600} size="sm">
                          {s.address}
                        </Anchor>
                        <Text size="xs" c="dimmed">
                          {s.partyNames.join(', ')}
                        </Text>
                      </div>
                    ))}
                  </Stack>
                </SectionCard>
                <SectionCard title="Sprawy" icon={<IconBriefcase size={16} />}>
                  <Stack gap={6}>
                    {res.data.cases.length === 0 && none}
                    {res.data.cases.map((c) => (
                      <div key={c.id}>
                        <Group gap={6}>
                          <Anchor component={Link} to={`/sprawy/${c.id}`} fw={600} size="sm">
                            {c.number}
                          </Anchor>
                          <CaseStatusBadge status={c.status} />
                        </Group>
                        <Text size="xs" c="dimmed">
                          {[c.clientName, c.title].filter(Boolean).join(' · ')}
                        </Text>
                      </div>
                    ))}
                  </Stack>
                </SectionCard>
                <SectionCard title="Prace" icon={<IconTool size={16} />}>
                  <Stack gap={6}>
                    {res.data.works.length === 0 && none}
                    {res.data.works.map((w) => (
                      <div key={w.id}>
                        <Group gap={6}>
                          <Anchor component={Link} to={w.caseId ? `/sprawy/${w.caseId}?zakladka=prace` : '/serwis'} fw={600} size="sm">
                            {taskLabel(w)} {w.number}
                          </Anchor>
                          <TaskStatusBadge status={w.status} />
                        </Group>
                        <Text size="xs" fw={500}>
                          {workTerm(w)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {[w.clientName, w.address, w.caseNumber].filter(Boolean).join(' · ')}
                        </Text>
                      </div>
                    ))}
                  </Stack>
                </SectionCard>
                <SectionCard title="Zadania" icon={<IconChecklist size={16} />}>
                  <Stack gap={6}>
                    {res.data.tasks.length === 0 && none}
                    {res.data.tasks.map((t) => (
                      <div key={t.id}>
                        <Anchor component={Link} to={`/sprawy/${t.caseId}?zakladka=zadania`} fw={600} size="sm">
                          {t.title}
                        </Anchor>
                        <Text size="xs" c="dimmed">
                          {[t.caseNumber, t.clientName, t.assigneeName ?? 'do przejęcia', t.dueAt ? `termin ${formatDateTime(t.dueAt)}` : null].filter(Boolean).join(' · ')}
                        </Text>
                      </div>
                    ))}
                  </Stack>
                </SectionCard>
              </SimpleGrid>
            )
          )}
        </QueryState>
      )}
    </>
  );
}
